/* eslint-disable */

import { partyHorn } from "@simplybusiness/icons";
import {
  Alert,
  Button,
  Icon,
  RadioGroup,
  Segment,
  Text,
} from "@simplybusiness/mobius";
import { useContext, useState } from "react";
import { CombinedPaymentContext } from "../CombinedPaymentContext";
import { DialogueModal } from "../DialogueModal";
import { currencyFormatter, fetchRequest } from "../UtilsHelper";
import { PaymentOption } from "./PaymentOption";

const ERRORS = {
  CART_ITEM_NOT_FOUND:
    "We encountered an issue updating your payment option. Please try selecting the same option again in a few moments.",
  SERVER_ERROR:
    "We encountered an unexpected issue. Please try again in a few moments or try refreshing the page.",
};

const paymentOptions = { ANNUAL: "annual", MONTHLY: "monthly" };

type InstallmentData = {
  downpayment?: number;
  count?: number;
  amount?: number;
  total?: number;
};

type InstallmentProps = {
  count: number;
  amount: number;
  total: number;
};

export const PaymentOptions = ({
  annual,
  monthly,
  selectedOption = "",
  currency,
  fireEvent,
  cartItemReference,
  markedForPurchase,
  onUpdateItem,
  promptInformation,
  enablePaymentOptionsServerUpdate,
  hasMonthlyFees,
  onPaymentOptionsClientUpdate,
}: PaymentOptionsProps) => {
  let installmentData: InstallmentData = {} as InstallmentProps;
  const [completeQuote, setIsCompleteQuote] = useState(false);
  const [removeProduct, setIsRemovedProduct] = useState(false);
  const [updatingPaymentOption, setUpdatingPaymentOption] = useState(false);
  const [isError, setIsError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const isCombinedPaymentPage = useContext(CombinedPaymentContext);

  if (
    monthly &&
    Object.keys(monthly).length > 0 &&
    monthly.installmentsSchedule.length > 0
  ) {
    // Calculate total from installments if totalPayable is not provided
    const calculatedTotal =
      monthly.totalPayable ??
      monthly.downpaymentAmount +
        monthly.installmentsSchedule.reduce(
          (sum, installment) => sum + installment.amount,
          0,
        );

    installmentData = {
      downpayment: monthly.downpaymentAmount,
      count: monthly.installmentsSchedule.length,
      amount: monthly.installmentsSchedule[0].amount,
      total: calculatedTotal,
    };
  }

  const calculateMonthlySavings = () => {
    if (installmentData.total) {
      return installmentData.total - annual.amount;
    }
    return 0;
  };

  const savingsAmount = calculateMonthlySavings();

  const promptButton = (
    className: string | undefined,
    loading: boolean | undefined,
    buttonText: string,
    onClickFn = () => {},
  ) => (
    <Button
      variant="secondary"
      size="sm"
      className={className}
      isLoading={loading}
      isDisabled={loading}
      onClick={() => onClickFn()}
    >
      {buttonText}
    </Button>
  );

  const removeFromCart = async (itemReference: string) => {
    const url = `?step_action=update_cart_item`;
    const payload = JSON.stringify({
      item_id: itemReference,
      action: "remove",
    });

    const response = await fetchRequest(url, payload);
    onUpdateItem(response, itemReference);
    if (response) setIsRemovedProduct(false);
  };

  const removeFromCartAndRedirect = async (
    itemReference: string,
    redirectUrl: string,
  ) => {
    const url = `?step_action=update_cart_item`;
    const payload = JSON.stringify({
      item_id: itemReference,
      action: "remove",
    });

    const response = await fetchRequest(url, payload);
    if (response) window.location.href = redirectUrl;
  };
  const handleServerPaymentOptionsChange = async (paymentOption: string) => {
    let response;
    try {
      setIsError(false);
      setUpdatingPaymentOption(true);
      fireEvent(cartItemReference, paymentOption);
      const url = `?step_action=update_cart_item`;
      const payload = JSON.stringify({
        action: "update_payment_option",
        item_id: cartItemReference,
        payment_option: paymentOption,
      });
      response = await fetchRequest(url, payload);
      if (!response.success) {
        const message: string =
          response.message || "Error updating payment option";
        throw new Error(message);
      }
      onUpdateItem(response, cartItemReference);
    } catch {
      if (
        response &&
        response.code === 404 &&
        response.productsInformation &&
        response.totalPaymentAmount
      ) {
        setErrorMessage(ERRORS.CART_ITEM_NOT_FOUND);
        onUpdateItem(response, cartItemReference);
      } else {
        setErrorMessage(ERRORS.SERVER_ERROR);
      }
      setIsError(true);
    } finally {
      setUpdatingPaymentOption(false);
    }
  };
  const handlePaymentOptionsChange = async (paymentOption: string) => {
    if (enablePaymentOptionsServerUpdate) {
      await handleServerPaymentOptionsChange(paymentOption);
    } else {
      if (onPaymentOptionsClientUpdate) {
        onPaymentOptionsClientUpdate(cartItemReference, paymentOption);
      }
    }
  };

  return !promptInformation.promptCustomer ? (
    <Segment
      className={`subtle ${!markedForPurchase && "exclude-for-purchase"} ${isCombinedPaymentPage && "combined-styles"}`}
    >
      <Text elementType="p" data-testid="payment-options-title">
        Payment Options
      </Text>
      {isCombinedPaymentPage ? (
        <RadioGroup
          className="sb-brand"
          name={`payment_options[${cartItemReference}]`}
          data-testid="radio-group"
          defaultValue={selectedOption}
          aria-label="Payment options"
          onChange={e => {
            handlePaymentOptionsChange(e.target.value).catch(error => {
              console.error("Error handling payment options change:", error);
            });
          }}
          isDisabled={updatingPaymentOption}
          isInvalid={isError}
          errorMessage={isError ? errorMessage : undefined}
        >
          <PaymentOption
            label="Pay monthly"
            value={paymentOptions.MONTHLY}
            toPayToday={installmentData.downpayment || null}
            currency={currency}
            installment={installmentData as InstallmentProps}
          />
          <PaymentOption
            label="Pay annually"
            value={paymentOptions.ANNUAL}
            toPayToday={annual.amount}
            currency={currency}
          />
        </RadioGroup>
      ) : (
        <RadioGroup
          className="sb-brand"
          name={`payment_options[${cartItemReference}]`}
          data-testid="radio-group"
          defaultValue={selectedOption}
          aria-label="Payment options"
          onChange={e => {
            handlePaymentOptionsChange(e.target.value).catch(error => {
              console.error("Error handling payment options change:", error);
            });
          }}
          isDisabled={updatingPaymentOption}
          isInvalid={isError}
          errorMessage={isError ? errorMessage : undefined}
        >
          <PaymentOption
            label="Pay annually"
            value={paymentOptions.ANNUAL}
            toPayToday={annual.amount}
            currency={currency}
          />
          <PaymentOption
            label="Pay monthly"
            value={paymentOptions.MONTHLY}
            toPayToday={installmentData.downpayment || null}
            currency={currency}
            installment={installmentData as InstallmentProps}
            hasMonthlyFees={hasMonthlyFees}
          />
        </RadioGroup>
      )}
      {savingsAmount > 0 && (
        <Text variant="small" className="savings-message-success">
          <Icon icon={partyHorn} />
          &nbsp;You save {currencyFormatter(currency).format(savingsAmount)} by
          paying annually
        </Text>
      )}
    </Segment>
  ) : (
    <Segment className="non-eligible-item subtle">
      <div className="mobius-payment-summary__styled-div">
        <Alert header="Action required" variant="error">
          {promptInformation.message}
        </Alert>

        {promptInformation.type === "completeQuote" ? (
          promptButton(
            "incomplete-quote-actions",
            completeQuote,
            promptInformation.buttonText,
            () => {
              setIsCompleteQuote(true);
              void removeFromCartAndRedirect(
                cartItemReference,
                promptInformation.actionUrl,
              );
            },
          )
        ) : (
          <DialogueModal
            cancelButtonText="Cancel"
            headerText="Resync your quote"
            actionButtonText="Resync your quote"
            description="In resyncing your coverage you will be taken back to your original quote selection."
            redirectFunction={() => {
              setIsCompleteQuote(true);
              void removeFromCartAndRedirect(
                cartItemReference,
                promptInformation.actionUrl,
              );
            }}
            button={promptButton(
              "incomplete-quote-actions",
              completeQuote,
              promptInformation.buttonText,
            )}
          />
        )}
        <Button
          variant="ghost"
          size="sm"
          className="incomplete-quote-actions"
          isLoading={removeProduct}
          isDisabled={removeProduct}
          onPress={() => {
            setIsRemovedProduct(true);
            void removeFromCart(cartItemReference);
          }}
        >
          Remove this coverage
        </Button>
      </div>
    </Segment>
  );
};

type PromptInformation = {
  promptCustomer: boolean;
  message: string;
  actionUrl: string;
  buttonText: string;
  type: string;
};

export type PaymentOptionsProps = {
  annual: { amount: number };
  monthly: {
    downpaymentAmount: number;
    firstInstallmentDate: string;
    installmentsSchedule: { month: string; year: string; amount: number }[];
    totalPayable?: number | null;
  };
  fireEvent: (cartItemReference: string, paymentOption: string) => void;
  selectedOption?: string;
  currency: string;
  cartItemReference: string;
  markedForPurchase: boolean;
  promptInformation: PromptInformation;
  onUpdateItem: (response: any, cartItemReference: string) => void;
  enablePaymentOptionsServerUpdate: boolean;
  hasMonthlyFees?: boolean;
  onPaymentOptionsClientUpdate?: (
    cartItemReference: string,
    paymentOption: string,
  ) => void;
};
